import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, StarRating, Loading, EmptyState, VerifiedBadge } from "@/components/shared";
import { FileText, Bell, ShieldCheck, Bookmark, Flag } from "lucide-react";

export default function Dashboard() {
  const { user, notifications } = useAuth();
  const nav = useNavigate();
  const [reviews, setReviews] = useState(null);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (user) {
      api.get("/reviews/mine").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
      api.get("/me/saved").then(({ data }) => setSaved(data)).catch(() => setSaved([]));
    }
  }, [user, nav]);

  if (user === null || (user && reviews === null)) return <Loading />;
  if (!user) return null;

  const counts = {
    under_review: reviews.filter((r) => r.status === "under_review").length,
    published: reviews.filter((r) => r.status === "published").length,
    declined: reviews.filter((r) => r.status === "declined").length,
  };

  return (
    <main className="mx-auto max-w-5xl px-5 py-10 lg:px-8">
      <p className="eyebrow mb-2">Your account</p>
      <h1 className="text-3xl font-bold">Hi, {user.name?.split(" ")[0]}</h1>

      <div className="mt-8 grid grid-cols-3 gap-4">
        {[["Under Review", counts.under_review, "text-info-500"], ["Published", counts.published, "text-success-500"], ["Declined", counts.declined, "text-coral-500"]].map(([k, v, c]) => (
          <div key={k} className="card-surface p-5">
            <p className="text-xs uppercase tracking-wide text-slate-500">{k}</p>
            <p className={`mt-2 font-heading text-3xl font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="text-xl font-bold">My reviews</h2>
          {reviews.length === 0 ? (
            <div className="mt-4"><EmptyState icon={FileText} title="No reviews yet" subtitle="Share your first experience to help others."
              action={<Link to="/companies" className="btn-teal mt-2 text-sm">Browse companies</Link>} /></div>
          ) : (
            <div className="mt-4 space-y-4">
              {reviews.map((r) => (
                <div key={r.id} data-testid="my-review" className="card-surface p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/company/${r.company_id}`} className="font-semibold hover:text-teal-400">{r.title}</Link>
                        {r.verified_employee && <VerifiedBadge />}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{r.company_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="mt-3 flex items-center gap-2"><StarRating value={r.rating_overall} size={14} /><span className="text-xs text-slate-500">{r.rating_overall?.toFixed(1)}</span></div>
                  {r.status === "declined" && r.decline_reason && (
                    <p className="mt-3 rounded-lg border border-coral-500/30 bg-coral-500/5 px-3 py-2 text-sm text-coral-500">Reason: {r.decline_reason}</p>
                  )}
                  {r.status === "under_review" && (
                    <p className="mt-3 text-sm text-slate-400">Your review is being screened for spam & abuse, then reviewed by a moderator.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <aside>
          <h2 className="flex items-center gap-2 text-xl font-bold"><Bell className="h-5 w-5 text-teal-400" /> Activity</h2>
          <div className="mt-4 card-surface divide-y divide-[#E4E1D8] p-2">
            {notifications.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-400">No activity yet</p>
            ) : notifications.slice(0, 10).map((n) => (
              <div key={n.id} className="px-3 py-3 text-sm text-slate-300">{n.message}
                <p className="mt-1 text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
          {(user.role === "admin" || user.role === "moderator") && (
            <Link to="/moderation" data-testid="go-moderation" className="mt-4 flex items-center gap-2 rounded-xl border border-teal-400/30 bg-teal-400/5 p-4 text-sm text-teal-300 hover:bg-teal-400/10">
              <ShieldCheck className="h-5 w-5" /> Open Moderation Queue
            </Link>
          )}
        </aside>
      </div>

      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-bold"><Bookmark className="h-5 w-5 text-teal-400" /> Saved companies</h2>
        {saved.length === 0 ? (
          <div className="mt-4"><EmptyState icon={Bookmark} title="No saved companies yet" subtitle="Bookmark employers you're researching to keep them here."
            action={<Link to="/companies" className="btn-teal mt-2 text-sm">Browse companies</Link>} /></div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {saved.map((c) => (
              <Link key={c.id} to={`/company/${c.id}`} data-testid="saved-company" className="card-surface lift flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{c.name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{c.location || "-"} · {c.licensing_status}</p>
                  </div>
                  <span className="font-heading text-2xl font-bold text-teal-400">{c.avg_overall || "-"}</span>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[#DDDAD1] pt-3">
                  <StarRating value={c.avg_overall} size={13} />
                  <span className="flex items-center gap-3 text-xs text-slate-400">
                    {c.red_flag_count > 0 && <span className="flex items-center gap-1 text-coral-500"><Flag className="h-3.5 w-3.5" /> {c.red_flag_count}</span>}
                    {c.review_count} reviews
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
